import { apiClient, ApiError } from './apiClient'
import type { SwitchList } from '../types/interchange'
import type { SwitchJobTelemetry } from '../types/telemetry'
import type {
  SwitchListGenerateRequest,
  TrackCarInventory,
  WorkOrderSummary,
} from '../types/switchlist'

/**
 * EDI-independent switch list builder endpoints (Plan 24 P4/P5).
 */

/** POST /api/v1/switchlists/generate — plan-phase switch list from planned moves. */
export function generateSwitchList(
  payload: SwitchListGenerateRequest
): Promise<SwitchList> {
  return apiClient.post<SwitchList>('/api/v1/switchlists/generate', payload)
}

/** GET /api/v1/yards/{facilityId}/tracks/{trackId}/cars — ordered A-end → B-end. */
export function getTrackCars(
  facilityId: string,
  trackId: string
): Promise<TrackCarInventory> {
  return apiClient.get<TrackCarInventory>(
    `/api/v1/yards/${facilityId}/tracks/${trackId}/cars`
  )
}

/** GET /api/v1/workorders?facilityId&status — work order queue. */
export function getWorkOrders(
  facilityId?: string,
  status?: string[]
): Promise<WorkOrderSummary[]> {
  const params = new URLSearchParams()
  if (facilityId) {
    params.set('facilityId', facilityId)
  }
  status?.forEach((s) => params.append('status', s))
  const qs = params.toString()
  return apiClient.get<WorkOrderSummary[]>(`/api/v1/workorders${qs ? `?${qs}` : ''}`)
}

/**
 * GET /api/v1/switchlists/telemetry/{workOrderId} — latest telemetry revision.
 * Server picks the DTO by role (SUPERADMIN → full trace). 404 (no row, or the
 * row isn't in the caller's facility scope) maps to null so the story UI can
 * render "Telemetry Summary Unavailable" instead of an error.
 */
export async function getSwitchJobTelemetry(
  workOrderId: string
): Promise<SwitchJobTelemetry | null> {
  try {
    return await apiClient.get<SwitchJobTelemetry>(
      `/api/v1/switchlists/telemetry/${workOrderId}`
    )
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return null
    }
    throw e
  }
}
